import { create } from "zustand";

type TimerState = {
  isTimerRunning: boolean;
  /** 開始時刻のUNIX ms（再開のたびに更新） */
  startedAt: number | null;
  /** これまでの累積経過ms（停止→再開で引き継ぐ） */
  accumulatedMs: number;
  /** 表示用のスナップショット（UIを更新するための値） */
  displaySeconds: number;
  /** 内部ループの停止用 */
  _rafId: number | null;
  _worker?: Worker;

  startTimer: () => void;
  pauseTimer: () => void;
  resetTimer: () => void;
  getDisplayTime: () => string;
};

export const useTimerStore = create<TimerState>((set, get) => {
  // rAFで可視時に滑らか更新 + WebWorkerで背景でも1秒ごとにtick
  const ensureWorker = () => {
    let w = get()._worker;
    if (w) return w;
    const code = `
      let t = null;
      function tick() {
        postMessage('tick');
        // 背景ではclampされてもOK。表示更新が遅れても差分計算は正確
        t = setTimeout(tick, 1000);
      }
      onmessage = (e) => {
        if (e.data === 'start') { if (!t) tick(); }
        if (e.data === 'stop')  { if (t) { clearTimeout(t); t = null; } }
      };
    `;
    w = new Worker(
      URL.createObjectURL(new Blob([code], { type: "text/javascript" })),
    );
    w.onmessage = () => {
      const { isTimerRunning, startedAt, accumulatedMs } = get();
      if (!(isTimerRunning && startedAt)) return;
      const elapsed = Date.now() - startedAt + accumulatedMs;
      set({ displaySeconds: Math.floor(elapsed / 1000) });
    };
    set({ _worker: w });
    return w;
  };

  const startRaf = () => {
    const loop = () => {
      const { isTimerRunning, startedAt, accumulatedMs } = get();
      if (!(isTimerRunning && startedAt)) return;
      const elapsed = Date.now() - startedAt + accumulatedMs;
      set({ displaySeconds: Math.floor(elapsed / 1000) });
      const id = requestAnimationFrame(loop);
      set({ _rafId: id });
    };
    const id = requestAnimationFrame(loop);
    set({ _rafId: id });
  };

  // 可視状態のときはrAF、非可視のときは止めてもOK（workerがバックアップ更新）
  const onVisibility = () => {
    const { _rafId, isTimerRunning } = get();
    if (document.hidden) {
      if (_rafId) {
        cancelAnimationFrame(_rafId);
        set({ _rafId: null });
      }
    } else {
      if (isTimerRunning) startRaf();
      // 復帰直後に一発最新化
      const { startedAt, accumulatedMs } = get();
      if (startedAt) {
        const elapsed = Date.now() - startedAt + accumulatedMs;
        set({ displaySeconds: Math.floor(elapsed / 1000) });
      }
    }
  };
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibility);
    document.addEventListener("visibilitychange", onVisibility);
  }

  return {
    isTimerRunning: false,
    startedAt: null,
    accumulatedMs: 0,
    displaySeconds: 0,
    _rafId: null,

    startTimer: () => {
      const { isTimerRunning } = get();
      if (isTimerRunning) return;

      set({ isTimerRunning: true, startedAt: Date.now() });

      // UI更新系を始動
      if (!document.hidden) startRaf();
      const worker = ensureWorker();
      worker.postMessage("start");
    },

    pauseTimer: () => {
      const { isTimerRunning, startedAt, accumulatedMs, _rafId, _worker } =
        get();
      if (!isTimerRunning) return;

      const now = Date.now();
      const newAccum = startedAt
        ? accumulatedMs + (now - startedAt)
        : accumulatedMs;

      if (_rafId) cancelAnimationFrame(_rafId);
      if (_worker) _worker.postMessage("stop");

      set({
        isTimerRunning: false,
        startedAt: null,
        accumulatedMs: newAccum,
        _rafId: null,
        displaySeconds: Math.floor(newAccum / 1000),
      });
    },

    resetTimer: () => {
      const { _rafId, _worker } = get();
      if (_rafId) cancelAnimationFrame(_rafId);
      if (_worker) _worker.postMessage("stop");

      set({
        isTimerRunning: false,
        startedAt: null,
        accumulatedMs: 0,
        displaySeconds: 0,
        _rafId: null,
      });
    },

    getDisplayTime: () => {
      const { displaySeconds } = get();
      const mins = Math.floor(displaySeconds / 60);
      const secs = displaySeconds % 60;
      return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    },
  };
});
